import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useCurrentUser } from './auth/useCurrentUser';
import LoginPage from './auth/LoginPage';
import AcceptInvitePage from './auth/AcceptInvitePage';
import ResetPasswordPage from './auth/ResetPasswordPage';
import DraftsListPage from './editor/DraftsListPage';
import EditorPage from './editor/EditorPage';
import UsersPage from './admin/UsersPage';
import EventsListPage from './events/EventsListPage';
import SignupPagesListPage from './signups/SignupPagesListPage';
import SignupPageEditorPage from './signups/SignupPageEditorPage';
import SubscribersPage from './subscribers/SubscribersPage';
import SubscriberDetailPage from './subscribers/SubscriberDetailPage';
import SendsListPage from './newsletter/SendsListPage';
import DigestPage from './pipeline/DigestPage';
import SourcesListPage from './pipeline/SourcesListPage';
import SourceEditorPage from './pipeline/SourceEditorPage';
import SendDetailPage from './newsletter/SendDetailPage';
import SettingsLayout from './settings/SettingsLayout';
import BlacklistPage from './settings/BlacklistPage';
import WelcomeEmailPage from './settings/WelcomeEmailPage';
import EmailProvidersListPage from './settings/EmailProvidersListPage';
import EmailProviderEditorPage from './settings/EmailProviderEditorPage';
import AccountPage from './auth/AccountPage';
import AppShell from './AppShell';
import type { User } from '@shared/types';

export default function App() {
  const { loading, user, passkeys, refresh, logout } = useCurrentUser();
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center text-slate-400 text-sm">Loading…</div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage onAuth={refresh} />} />
      <Route path="/accept-invite" element={user ? <Navigate to="/" replace /> : <AcceptInvitePage onAuth={refresh} />} />
      <Route path="/reset-password" element={<ResetPasswordPage onAuth={refresh} />} />

      {/* Authenticated routes */}
      <Route path="/" element={<RequireAuth user={user}><AppShell user={user!} onLogout={logout} /></RequireAuth>}>
        <Route index element={<DraftsListPage />} />
        <Route path="draft/:id" element={<EditorPage />} />
        <Route path="digest" element={<DigestPage />} />
        <Route path="digest/:date" element={<DigestPage />} />
        <Route path="events" element={<EventsListPage />} />
        <Route path="signups" element={<SignupPagesListPage />} />
        <Route path="signups/new" element={<SignupPageEditorPage />} />
        <Route path="signups/:id" element={<SignupPageEditorPage />} />
        <Route path="subscribers" element={<SubscribersPage />} />
        <Route path="subscribers/:id" element={<SubscriberDetailPage />} />
        <Route path="sends" element={<SendsListPage />} />
        <Route path="sends/:id" element={<SendDetailPage />} />
        <Route path="account" element={<AccountPage user={user!} passkeys={passkeys} onChange={refresh} />} />
        {/* Legacy redirect — Users moved into Settings. */}
        <Route path="admin/users" element={<Navigate to="/settings/users" replace />} />
        <Route
          path="settings"
          element={user?.role === 'admin' ? <SettingsLayout /> : <Navigate to="/" replace />}
        >
          <Route index element={<Navigate to="/settings/users" replace />} />
          <Route path="blacklist" element={<BlacklistPage />} />
          <Route path="sources" element={<SourcesListPage />} />
          <Route path="sources/new" element={<SourceEditorPage />} />
          <Route path="sources/:id" element={<SourceEditorPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="welcome-email" element={<WelcomeEmailPage />} />
          <Route path="email-providers" element={<EmailProvidersListPage />} />
          <Route path="email-providers/new" element={<EmailProviderEditorPage />} />
          <Route path="email-providers/:id" element={<EmailProviderEditorPage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to={user ? '/' : '/login'} replace />} />
    </Routes>
  );
}

function RequireAuth({ user, children }: { user: User | null; children: React.ReactNode }) {
  const location = useLocation();
  if (!user) {
    const search = new URLSearchParams({ from: location.pathname + location.search }).toString();
    return <Navigate to={`/login?${search}`} replace />;
  }
  return <>{children}</>;
}
